import { ModalSheet, SupportedCurrency } from '@mindly/ui-components';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementLocale } from '@stripe/stripe-js';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { environment } from 'src/enviroments';
import { PaymentCheckoutWidget } from 'src/widgets/PaymentCheckoutWidget';

type PaymentCheckoutModalFeatureProps = {
  chargeCurrency: SupportedCurrency;
} & React.ComponentProps<typeof ModalSheet> &
  Omit<React.ComponentProps<typeof PaymentCheckoutWidget>, 'onError' | 'onPaymentMethodChange' | 'onReady'>;

const stripe = loadStripe(environment.STRIPE_PUBLIC_KEY);

const PaymentCheckoutModalFeature: React.FC<PaymentCheckoutModalFeatureProps> = ({
  chargeAmount,
  amountBeforeDiscount,
  discountAmount,
  clientSecret,
  returnUrl,
  customerEmail,
  onSuccess,
  chargeCurrency,
  ...cardModalProps
}) => {
  const {
    i18n: { language },
  } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const { onPayNowButtonClick, ...restCardModalProps } = cardModalProps;

  const [isCardPaymentReady, setIsCardPaymentReady] = useState(false);
  const [activePaymentMethod, setActivePaymentMethod] = useState<'card' | 'one-click'>('one-click');

  const onReady = useCallback((paymentMethod: 'card' | 'one-click') => {
    if (paymentMethod === 'card') {
      setIsCardPaymentReady(true);
    }
  }, []);

  const onPaymentMethodChange = useCallback((paymentMethod: 'card' | 'one-click') => {
    setActivePaymentMethod(paymentMethod);
  }, []);

  useEffect(() => {
    if (activePaymentMethod === 'card' && isCardPaymentReady) {
      if (!contentRef.current) return;

      requestAnimationFrame(() => {
        contentRef.current?.scrollTo({
          top: contentRef.current.scrollHeight,
          behavior: 'smooth',
        });
      });
    }
  }, [isCardPaymentReady, activePaymentMethod]);

  return (
    <ModalSheet
      {...restCardModalProps}
      withCloseButton
      onDidDismiss={(event) => {
        restCardModalProps?.onDidDismiss?.(event);
      }}
      className="auto-height transition-height duration-300 ease-in-out"
    >
      <div ref={contentRef} className="overflow-y-auto max-h-[95vh]">
        <Elements
          stripe={stripe}
          options={{
            locale: language as StripeElementLocale,
            mode: 'payment',
            currency: chargeCurrency.toLowerCase(),
            amount: chargeAmount,
          }}
        >
          <PaymentCheckoutWidget
            chargeAmount={chargeAmount}
            discountAmount={discountAmount}
            amountBeforeDiscount={amountBeforeDiscount}
            onSuccess={onSuccess}
            clientSecret={clientSecret}
            returnUrl={returnUrl}
            customerEmail={customerEmail}
            onPayNowButtonClick={onPayNowButtonClick}
            onReady={onReady}
            onPaymentMethodChange={onPaymentMethodChange}
          />
        </Elements>
      </div>
    </ModalSheet>
  );
};

export default PaymentCheckoutModalFeature;
